"use strict";
/**
 * Copyright (c) 2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Joan Segura <joan.segura@rcsb.org>
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setFocusFromRange = setFocusFromRange;
exports.getStructureRefWithModelId = getStructureRefWithModelId;
exports.select = select;
exports.clearSelection = clearSelection;
exports.createComponent = createComponent;
exports.removeComponent = removeComponent;
const builder_1 = require("molstar/lib/mol-script/language/builder");
const structure_selection_query_1 = require("molstar/lib/mol-plugin-state/helpers/structure-selection-query");
const selection_1 = require("./selection");
function setFocusFromRange(plugin, target) {
    const data = getStructureWithModelId(plugin.managers.structure.hierarchy.current.structures, target);
    if (!data)
        return;
    const loci = (0, selection_1.targetToLoci)(target, data);
    if (!loci)
        return;
    plugin.managers.structure.focus.setFromLoci(loci);
}
function getStructureWithModelId(structures, target) {
    var _a, _b;
    const structureRef = getStructureRefWithModelId(structures, target);
    if (structureRef)
        return (_b = (_a = structureRef.cell) === null || _a === void 0 ? void 0 : _a.obj) === null || _b === void 0 ? void 0 : _b.data;
}
function getStructureRefWithModelId(structures, target) {
    var _a, _b, _c;
    for (const structure of structures) {
        if (!((_c = (_b = (_a = structure.cell) === null || _a === void 0 ? void 0 : _a.obj) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.units))
            continue;
        const unit = structure.cell.obj.data.units[0];
        if (unit.model.id === target.modelId)
            return structure;
    }
}
function select(plugin, targets, mode, modifier) {
    if (modifier === 'set')
        clearSelection(plugin, mode);
    (Array.isArray(targets) ? targets : [targets]).forEach((target, n) => {
        const structure = getStructureWithModelId(plugin.managers.structure.hierarchy.current.structures, target);
        if (!structure)
            return;
        const loci = (0, selection_1.targetToLoci)(target, structure);
        if (!loci)
            return;
        if (mode === 'hover') {
            plugin.managers.interactivity.lociHighlights.highlight({ loci });
        }
        else if (mode === 'select') {
            plugin.managers.structure.selection.fromLoci('add', loci);
        }
    });
}
function clearSelection(plugin, mode, target) {
    if (mode === 'hover') {
        plugin.managers.interactivity.lociHighlights.clearHighlights();
        return;
    }
    if (!target) {
        plugin.managers.interactivity.lociSelects.deselectAll();
        return;
    }
    const data = getStructureWithModelId(plugin.managers.structure.hierarchy.current.structures, target);
    if (!data)
        return;
    const loci = (0, selection_1.targetToLoci)(target, data);
    plugin.managers.interactivity.lociSelects.deselect({ loci });
}
async function createComponent(plugin, componentLabel, targets, representationType) {
    for (const target of (Array.isArray(targets) ? targets : [targets])) {
        const structureRef = getStructureRefWithModelId(plugin.managers.structure.hierarchy.current.structures, target);
        if (!structureRef)
            throw Error('createComponent error: model not found');
        const residues = toResidues(target);
        const sel = (0, structure_selection_query_1.StructureSelectionQuery)('innerQuery_' + Math.random().toString(36).substring(2), builder_1.MolScriptBuilder.struct.generator.atomGroups((0, selection_1.rangeToTest)(target.labelAsymId, residues, target.operatorName)));
        await plugin.managers.structure.component.add({
            selection: sel,
            options: { checkExisting: false, label: componentLabel },
            representation: representationType,
        }, [structureRef]);
    }
}
function toResidues(target) {
    if ('labelSeqRange' in target) {
        return (0, selection_1.toRange)(target.labelSeqRange.beg, target.labelSeqRange.end);
    }
    if ('labelSeqId' in target) {
        return [target.labelSeqId];
    }
    return [];
}
async function removeComponent(plugin, componentLabel) {
    const out = [];
    plugin.managers.structure.hierarchy.currentComponentGroups.forEach(c => {
        var _a;
        for (const comp of c) {
            if (((_a = comp.cell.obj) === null || _a === void 0 ? void 0 : _a.label) === componentLabel) {
                const o = plugin.managers.structure.hierarchy.remove(c);
                if (o)
                    out.push(o);
                break;
            }
        }
    });
    await Promise.all(out);
}
